import { useRoute, Link } from "wouter";
import {
  useGetAdminRaiser,
  getGetAdminRaiserQueryKey,
} from "@workspace/api-client-react";
import Navbar from "@/components/layout/Navbar";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@workspace/ui-web/components/ui/card";
import { Badge } from "@workspace/ui-web/components/ui/badge";
import {
  Loader2,
  ArrowLeft,
  Dog,
  MapPin,
  Phone,
  Calendar,
  Mail,
} from "lucide-react";
import { format } from "date-fns";

export default function AdminRaiserDetail() {
  const [, params] = useRoute("/admin/raisers/:id");
  const id = params?.id ? parseInt(params.id, 10) : 0;

  const { data: raiser, isLoading } = useGetAdminRaiser(id, {
    query: { enabled: !!id, queryKey: getGetAdminRaiserQueryKey(id) },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-muted/20">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (!raiser) {
    return (
      <div className="min-h-screen flex flex-col bg-muted/20">
        <Navbar />
        <div className="flex-1 flex items-center justify-center flex-col gap-4">
          <h2 className="text-2xl font-bold">Raiser not found</h2>
          <Link href="/admin" className="text-primary hover:underline">
            Return to dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-muted/20">
      <Navbar />
      <main className="flex-1 container px-4 py-8 mx-auto max-w-5xl">
        <div className="mb-6">
          <Link
            href="/admin"
            className="text-muted-foreground hover:text-foreground inline-flex items-center gap-2 text-sm font-medium"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Dashboard
          </Link>
        </div>

        <div className="grid md:grid-cols-[1fr_2fr] gap-8">
          {/* Sidebar Profile */}
          <div className="space-y-6">
            <Card>
              <CardHeader className="bg-primary/5 pb-4">
                <CardTitle className="text-2xl">
                  {raiser.name || "Unknown Name"}
                </CardTitle>
                <CardDescription className="flex items-center gap-2">
                  <Mail className="h-4 w-4" /> {raiser.email}
                </CardDescription>
                <div className="mt-2 pt-2">
                  <Badge>{raiser.role}</Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-6 space-y-4 text-sm">
                {!raiser.profile ? (
                  <div className="text-muted-foreground italic text-center py-4">
                    No profile created yet.
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between border-b pb-2">
                      <span className="text-muted-foreground">Dog</span>
                      <span className="font-semibold text-primary flex items-center gap-1">
                        <Dog className="h-4 w-4" />{" "}
                        {raiser.profile.dogName || "N/A"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between border-b pb-2">
                      <span className="text-muted-foreground">Breed</span>
                      <span className="font-medium">
                        {raiser.profile.dogBreed || "N/A"}
                      </span>
                    </div>
                    {raiser.profile.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-muted-foreground" />{" "}
                        {raiser.profile.phone}
                      </div>
                    )}
                    {raiser.profile.location && (
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-muted-foreground" />{" "}
                        {raiser.profile.location}
                      </div>
                    )}
                    {raiser.profile.startedAt && (
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        Started{" "}
                        {format(new Date(raiser.profile.startedAt), "MMM yyyy")}
                      </div>
                    )}
                    {raiser.profile.bio && (
                      <div className="pt-2">
                        <p className="text-xs text-muted-foreground leading-relaxed italic border-l-2 pl-3 py-1">
                          "{raiser.profile.bio}"
                        </p>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Updates List */}
          <div className="space-y-6">
            <h2 className="text-2xl font-bold flex items-center gap-2 border-b pb-4">
              Updates ({raiser.updates.length})
            </h2>

            {raiser.updates.length === 0 ? (
              <div className="bg-card rounded-xl border border-dashed p-12 text-center text-muted-foreground">
                No updates submitted by this raiser yet.
              </div>
            ) : (
              <div className="space-y-6">
                {raiser.updates.map((update) => (
                  <Card key={update.id} className="overflow-hidden">
                    <CardHeader className="bg-muted/20 border-b py-3 px-6">
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-foreground">
                          {format(
                            new Date(update.createdAt),
                            "MMMM d, yyyy h:mm a",
                          )}
                        </span>
                        {update.weightLbs && (
                          <Badge variant="secondary">
                            {update.weightLbs} lbs
                          </Badge>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="p-6">
                      <p className="whitespace-pre-wrap text-foreground/90">
                        {update.note}
                      </p>

                      {update.milestones.length > 0 && (
                        <div className="mt-4 flex flex-wrap gap-2">
                          {update.milestones.map((m, i) => (
                            <Badge
                              key={i}
                              variant="outline"
                              className="bg-background"
                            >
                              <span className="capitalize font-semibold mr-1">
                                {m.category}:
                              </span>{" "}
                              {m.description}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
